<template>
  <b-container>
    <b-row class="h-100">
      <b-col
        class="d-flex flex-column justify-content-center login-wrapper--right-form"
      >
        <div class="d-flex align-items-center">
          <div>
            <a href="https://www.tradiepass.com.au/">
              <b-img src="/img/logo.svg" fluid />
            </a>
          </div>
        </div>

        <div
          v-if="deletionNotice && !deletionNoticeDismissed"
          class="deletion-notice mt-4"
          role="status"
          :data-testid="'account-deletion-' + deletionNotice"
        >
          <b-icon
            icon="check-circle-fill"
            class="deletion-notice__icon"
            aria-hidden="true"
          ></b-icon>
          <!-- Tradie had a company: a 7-day notice period, restorable by logging in. -->
          <div
            v-if="deletionNotice === 'scheduled'"
            class="deletion-notice__body"
          >
            <h5 class="deletion-notice__title">
              Your Tradie Pass account is now being processed for deletion.
            </h5>
            <p>Your associated company has been notified where applicable.</p>
            <p>
              Some company records may be retained by the company where required
              or permitted by law or for legitimate business purposes.
            </p>
            <p>You don't need to take any further action.</p>
            <p class="mb-0">
              Log back in within 7 days to have your account and information
              restored.
            </p>
          </div>
          <!-- Tradie had no company: removed straight away, nothing to restore. -->
          <div v-else class="deletion-notice__body">
            <h5 class="deletion-notice__title">
              Your Tradie Pass account has been deleted.
            </h5>
            <p class="mb-0">
              Your account and information have been removed. If you think this
              is incorrect, contact
              <a href="mailto:support@tradiepass.com.au"
                >support@tradiepass.com.au</a
              >.
            </p>
          </div>
          <button
            type="button"
            class="deletion-notice__close"
            aria-label="Dismiss"
            @click="deletionNoticeDismissed = true"
          >
            &times;
          </button>
        </div>

        <b-alert
          :show="mfaLocked"
          variant="warning"
          class="mt-4 mb-0"
          data-testid="mfa-locked-notice"
        >
          Too many incorrect codes. Please sign in again.
        </b-alert>

        <div class="mt-4">
          <h3 class="font-weight-bold">Welcome to Tradie Pass</h3>
          <p class="mt-3">Login to continue using your account!</p>
        </div>

        <b-row no-gutters>
          <b-col cols="12">
            <!-- HubSpot-visible form: only email + hidden log_in flag live inside the form tag -->
            <b-form id="login-form" @submit.prevent="onSubmit">
              <!-- Hidden marker for HubSpot collected forms -->
              <input type="hidden" name="log_in" value="true" />

              <b-form-group id="input-group-1" class="col-12 mb-4">
                <label class="font-weight-bold">Email</label>
                <span class="focus-border"></span>
                <b-form-input
                  id="input-1"
                  v-model="form.username"
                  @change="handleUsernameBlur"
                  :class="form.username ? 'has-content' : ''"
                  type="text"
                  required
                  autofocus
                  placeholder="Type in email"
                />
                <div v-if="form.$getError('email')" class="input-error">
                  {{ form.$getError('email')[0] }}
                </div>
              </b-form-group>
            </b-form>

            <!-- Password lives outside the form so HubSpot cannot scrape it,
                but remains in the same column visually. -->
            <b-form-group id="input-group-2" class="col-12 mb-3">
              <label class="font-weight-bold">Password</label>
              <span class="focus-border"></span>

              <div class="password-wrapper">
                <b-form-input
                  id="input-2"
                  v-model="form.password"
                  :class="form.password ? 'has-content' : ''"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  required
                  placeholder="Password"
                  @keyup.enter="onSubmit"
                ></b-form-input>

                <b-icon
                  v-if="form.password"
                  :icon="showPassword ? 'eye-slash' : 'eye'"
                  class="password-toggle"
                  @click="showPassword = !showPassword"
                />
              </div>
            </b-form-group>

            <div class="mb-4 text-danger" v-show="errorMessage">
              {{ errorMessage }}
            </div>

            <div class="form-link-cont">
              <b-link class="form-link" to="/forgot-password">
                Forgot password?
              </b-link>
            </div>
          </b-col>

          <b-col cols="12">
            <b-button
              type="submit"
              variant="primary"
              block
              size="lg"
              form="login-form"
              :disabled="lockoutSeconds > 0"
            >
              <b-spinner v-if="form.$busy" small label="Loading" />
              Log in
            </b-button>
          </b-col>
        </b-row>

        <div class="text-center">
          <span>Don't have an account?</span>
          <router-link :to="'/signup'">
            <!--"{ name: 'auth.signup-page' }">-->
            Sign up
          </router-link>
        </div>
      </b-col>
    </b-row>
    <b-modal
      v-model="showArchivedModal"
      title="Account Archived"
      ok-only
      ok-title="OK"
      no-close-on-backdrop
      no-close-on-esc
      centered
    >
      <p>
        Your account has been archived and you cannot log in at this time.
        Please contact your Business Admin to have your account un-archived.
      </p>
    </b-modal>

    <b-modal
      v-model="retentionModal.show"
      title="Account Deactivated"
      ok-only
      ok-title="Close"
      no-close-on-backdrop
      no-close-on-esc
      centered
    >
      <p class="retention-p">
        <strong>{{ retentionModal.companyName }}</strong> has been deactivated.
        Your data will be permanently deleted in
        <strong>{{ retentionModal.daysRemaining }} days</strong> on
        {{ retentionModal.expiresAt }}.
      </p>
      <p class="retention-p mb-0">
        To restore your account, please contact us at
        <a :href="`mailto:${retentionModal.contactEmail}`">{{
          retentionModal.contactEmail
        }}</a
        >.
      </p>
    </b-modal>

    <!-- BA self-deleted with no replacement (Case 2) — must contact support -->
    <b-modal
      v-model="baRetentionModal.show"
      title="Account Deactivated"
      ok-only
      ok-title="Close"
      no-close-on-backdrop
      no-close-on-esc
      centered
    >
      <p class="retention-p">
        Your Business Admin account has been deactivated. Your data will be
        permanently deleted on
        <strong>{{ baRetentionModal.expiresAt }}</strong
        >.
      </p>
      <p class="retention-p mb-0">
        To restore your account, please contact TradiePass support at
        <a :href="`mailto:${baRetentionModal.contactEmail}`">{{
          baRetentionModal.contactEmail
        }}</a
        >.
      </p>
    </b-modal>
  </b-container>
</template>

<script>
import Form from '@/utils/form'
import dayjs from 'dayjs'
import { normalizeEmail } from '@/utils/emailPolicy'
// import SocialLogin from '@/components/SocialLogin'
// import LoginWithGoogle from '@/components/buttons/LoginWithGoogle'
// import LoginWithFacebook from '@/components/buttons/LoginWithFacebook'
// import LoginWithApple from '@/components/buttons/LoginWithApple'

export default {
  components: {
    // SocialLogin,
    // LoginWithGoogle,
    // LoginWithFacebook,
    // LoginWithApple,
  },
  data() {
    return {
      loading: false,
      showPassword: false,
      showArchivedModal: false,
      deletionNoticeDismissed: false,
      lockoutSeconds: 0,
      lockoutTimer: null,
      retentionModal: {
        show: false,
        companyName: '',
        daysRemaining: 0,
        expiresAt: '',
        contactEmail: '',
      },
      baRetentionModal: {
        show: false,
        expiresAt: '',
        contactEmail: '',
      },
      form: new Form({
        username: '', // this accepts email or phone number in API; for web app, email should suffice
        email: '',
        password: '',
        password_confirmation: '',
      }),
    }
  },

  computed: {
    // Set by auth/deleteAccount after a tradie deletes their account:
    // 'scheduled' (7-day notice period) or 'deleted' (removed straight away).
    deletionNotice() {
      const value = this.$route.query && this.$route.query.deletion
      return value === 'scheduled' || value === 'deleted' ? value : null
    },

    // Set by MfaPage when the API revokes the login after too many wrong codes.
    mfaLocked() {
      return Boolean(this.$route.query && this.$route.query.mfa === 'locked')
    },

    errorMessage() {
      if (this.lockoutSeconds > 0) {
        const unit = this.lockoutSeconds === 1 ? 'second' : 'seconds'
        return `Too many attempts. Please try again in ${this.lockoutSeconds} ${unit}.`
      }
      return this.form.$errorMessage
    },
  },

  created() {
    // Reaching the login page always means "start a fresh session". On a hard
    // refresh the token rehydrates from the cookie, so without this a
    // half-finished session (e.g. a first-login business admin who bailed out
    // of /change-password) lingers and the route guard just bounces them back.
    this.$store.commit('auth/clearToken')
    this.$store.commit('auth/clearUser')
  },

  beforeDestroy() {
    clearInterval(this.lockoutTimer)
  },

  methods: {
    startLockout(seconds) {
      clearInterval(this.lockoutTimer)
      this.lockoutSeconds = seconds
      this.lockoutTimer = setInterval(() => {
        if (this.lockoutSeconds <= 1) {
          this.clearLockout()
        } else {
          this.lockoutSeconds -= 1
        }
      }, 1000)
    },
    clearLockout() {
      clearInterval(this.lockoutTimer)
      this.lockoutTimer = null
      this.lockoutSeconds = 0
      this.form.$setErrorMessage('')
    },
    handleUsernameBlur(val) {
      // "username" accepts email or phone number. Only trim/lowercase-domain
      // when it looks like an email attempt - a phone number's digits/+
      // shouldn't be touched, and this must never reject input outright
      // (the API is the source of truth for whether it's valid).
      if (typeof val === 'string' && val.includes('@')) {
        this.form.username = normalizeEmail(val)
      } else if (typeof val === 'string') {
        this.form.username = val.trim()
      }
    },
    async onSubmit(evt) {
      if (this.lockoutSeconds > 0) return

      // Ensure password input is of type="password" at submit time,
      // so tools like HubSpot collected forms are less likely to treat it as a normal text field.
      this.showPassword = false

      // Ensure password input is of type="password" at submit time,
      // so tools like HubSpot collected forms are less likely to treat it as a normal text field.
      this.showPassword = false

      this.form.$busy = true

      await this.$api
        .post('auth/login', this.form.$data())
        .then(({ data }) => {
          // response payload
          const payload = data.data

          this.$store.commit('auth/setToken', payload)

          // if mfa required, store in mfa state
          if (payload.mfa_required) {
            const attemptsRemaining =
              typeof payload.attempts_remaining === 'number'
                ? payload.attempts_remaining
                : null
            const tokenIdValue =
              payload.token_id !== undefined && payload.token_id !== null
                ? payload.token_id
                : payload.tokenId || null

            this.$store.commit('auth/setMfaState', {
              channel: payload.mfa_channel || null,
              otp_expires_at: payload.otp_expires_at || null,
              token_id: tokenIdValue,
              attempts_remaining: attemptsRemaining,
            })
            this.$router.replace({ name: 'auth.mfa-page' })
            return
          }
          // Login Guard, only allow Tradie / Business Admin users
          const allowed = ['tradie', 'business_admin']
          if (!allowed.includes(data.data.user.user_type)) {
            const error = {
              response: {
                data: {
                  message:
                    "We couldn't find any records that matches your credentials.",
                },
                status: 401,
              },
            }
            throw error
          }
          if (data.data.user.is_archived) {
            this.showArchivedModal = true
            this.form.$busy = false
            return
          }

          this.$store.commit('auth/setToken', data.data)
          this.$store.commit('auth/setUser', data.data.user)

          this.$router
            .replace({
              name:
                data.data.user.user_type === 'business_admin' &&
                data.data.user.first_login
                  ? 'auth.change-password-page'
                  : 'home.welcome-page',
            })
            .catch(() => {})
        })
        .catch((error) => {
          const data = (error && error.response && error.response.data) || {}

          if (data.error_code === 'COMPANY_UNDER_RETENTION') {
            const expiresAt = dayjs(data.expires_at)
            this.retentionModal = {
              show: true,
              companyName: data.company_name || 'Your company',
              daysRemaining: Math.max(0, expiresAt.diff(dayjs(), 'day')),
              expiresAt: expiresAt.format('D MMM YYYY'),
              contactEmail: data.contact_email || 'support@tradiepass.com.au',
            }
            this.form.$busy = false
            return
          }

          if (data.error_code === 'BA_UNDER_RETENTION') {
            const expiresAt = dayjs(data.expires_at)
            this.baRetentionModal = {
              show: true,
              expiresAt: expiresAt.format('D MMM YYYY'),
              contactEmail: data.contact_email || 'support@tradiepass.com.au',
            }
            this.form.$busy = false
            return
          }

          if (data.error_code === 'ACCOUNT_ARCHIVED') {
            this.showArchivedModal = true
            this.form.$busy = false
            return
          }

          this.form.$handleError(error)

          if (data.retry_after > 0) {
            this.startLockout(data.retry_after)
          }
        })
        .finally(() => (this.form.$busy = false))
    },

    async onSuccess(payload) {
      if (this.loading) return
      this.loading = true
      await this.$api
        .post('/auth/social', payload)
        .then(({ data }) => {
          this.loading = false
          this.$store.commit('auth/setToken', data.data)
          this.$store.commit('auth/setUser', data.data.user)
          this.$router.replace({ name: 'home.welcome-page' }).catch(() => {})
        })
        .catch((error) => {
          this.signOut()
          this.form.$handleError(error)
          this.loading = false
        })
    },

    async signOut() {
      if (window.gapi) {
        window.gapi.auth2.getAuthInstance().signOut()
      }
      if (window.FB) {
        window.FB.logout()
      }
      await this.$api.post('auth/logout')
    },
  },

  watch: {
    'form.username'() {
      this.form.$clearError('username')
      // The API locks per email, so a different one can try straight away.
      if (this.lockoutSeconds > 0) {
        this.clearLockout()
      }
    },
    'form.email'() {
      this.form.$clearError('email')
    },
    'form.password'() {
      this.form.$clearError('password')
    },
  },
}
</script>

<style lang="scss" scoped>
// Shown after a tradie schedules account deletion (see auth/deleteAccount).
.deletion-notice {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 16px 40px 16px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #006cff;
  border-radius: 8px;
  box-shadow: 0px 10px 25px #00429d0d;

  &__icon {
    flex: 0 0 auto;
    margin-top: 3px;
    margin-right: 12px;
    font-size: 1.25rem;
    color: #006cff;
  }

  &__title {
    margin: 0 0 8px;
    font-weight: 700;
    color: #111827;
  }

  &__body p {
    margin: 0 0 6px;
    color: #374151;
    line-height: 1.5;
  }

  &__close {
    position: absolute;
    top: 8px;
    right: 12px;
    padding: 0 4px;
    font-size: 1.5rem;
    line-height: 1;
    color: #6b7280;
    background: transparent;
    border: 0;
    cursor: pointer;

    &:hover {
      color: #111827;
    }
  }
}

.retention-p {
  font-size: 1.05rem;
  line-height: 1.7;
}

html,
body {
  height: 100%;
  margin: 0px;
  background-color: red;
}

.container {
  height: 100%;
}

.apple-container {
  @media (max-width: 767px) {
    border-radius: 8px;
    border: 2px solid #e5e5e5 !important;

    &:hover {
      box-shadow: 0px 12px 20px #00000014 !important;
    }
  }
}

.emoji-tool::before {
  content: '\1F6E0';
  font-size: 1.5em;
  margin-right: 0.5em;
}

.password-wrapper {
  position: relative;
}

.password-wrapper input {
  padding-right: 45px; // space for icon
}

.password-toggle {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #9aa0a6;
  transition: color 0.2s ease;
}

.password-toggle:hover {
  color: #495057;
}

.password-wrapper input::-ms-reveal {
  display: none;
}
</style>
